import { yupResolver } from "@hookform/resolvers/yup";
import { Button, ButtonVariant } from "@src/components/button/Button";
import { Input } from "@src/components/input/Input";
import { Switch } from "@src/components/switch/Switch";
import { useFirstTimeInteraction } from "@src/hooks/useFirstInteraction";
import { pricingActions } from "@src/store";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { SplitListingAdvanced, SplitListingAdvancedType } from "@src/validation/SplitListingAdvanced";
import { SplitListingValidation, SplitListingValidationType } from "@src/validation/SplitListingValidation";
import React, { useEffect, useRef } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import { IAdvancedTab } from "../../AdvancedTab";
import { SubHeader } from "../SubHeader/SubHeader";
import styles from "./SplitListing.module.scss";
import { HighlightedSectionEnum } from "@src/store/pricing/selectionSlice";
import useHighlightedSection from "@src/hooks/useHighlightedSection";
import useScrollToBottom from "@src/hooks/useScrollToBottom";
import { MixpanelCategory, mixpanelTrack } from "@src/components/utils/mixPanelUtils";

export const SplitListing: React.FC<IAdvancedTab> = ({ inventoryItem, onSuccess }) => {
	const allInventoryItems = useAppSelector((state) => state.pricing.eventInventoryAll.data);
	const Quantity = allInventoryItems.filter((x) => x.Listing_ID === inventoryItem?.Listing_ID).length;
	const dispatch = useAppDispatch();

	const currentPrice = Number(inventoryItem?.Item_Price ?? 0);

	const defaultValues = {
		Quantity: 1,
		Split_In_Groups: false,
		Ticket_Price: currentPrice + 1,
	};

	const { handleSubmit, control, watch, setValue, formState, reset } = useForm({
		mode: "onChange",
		resolver: yupResolver(SplitListingValidation),
		context: { maxQuantity: Quantity - 1, currentPrice },
		defaultValues,
	});

	const {
		handleSubmit: hsAdvanced,
		control: cAdvanced,
		formState: fsAdvanced,
		watch: fsWatch,
		reset: rAdvanced,
	} = useForm({
		mode: "onChange",
		resolver: yupResolver(SplitListingAdvanced),
		context: { maxQuantity: Quantity },
		defaultValues: {
			Number_Of_Groups: 2,
		},
	});
	const splitInGroups = watch("Split_In_Groups");
	const quantity = watch("Quantity");
	const ticketPrice = watch("Ticket_Price");
	const numOfGroups = fsWatch("Number_Of_Groups");

	const { isSubmitting: submittingAdvanced } = fsAdvanced;
	const { isSubmitting } = formState;

	const onSplitsChange = (data: boolean) => {
		setValue("Split_In_Groups", data, { shouldDirty: true });
	};

	const onSubmit: SubmitHandler<SplitListingValidationType> = async (data) => {
		if (!inventoryItem?.Listing_ID) {
			toast.warning("Listing ID doesn't exist");
			return;
		}
		mixpanelTrack(MixpanelCategory.SPLIT_LISTING);
		const dataToSend = {
			ListingId: String(inventoryItem?.Listing_ID),
			PriceOfGroup: String(data.Ticket_Price),
			QuantityToSplit: String(data.Quantity),
		};

		try {
			await dispatch(pricingActions.updateSplitListing(dataToSend)).unwrap();
			onSuccess?.();
		} catch (error) {
			console.log(error);
		}
	};

	const onAdvancedSubmit: SubmitHandler<SplitListingAdvancedType> = async (data) => {
		if (!inventoryItem?.Listing_ID) {
			toast.warning("Listing ID doesn't exist");
			return;
		}

		mixpanelTrack(MixpanelCategory.SPLIT_LISTING);

		const dataToSend = {
			ListingId: String(inventoryItem?.Listing_ID),
			NumGroups: String(data.Number_Of_Groups),
		};

		try {
			await dispatch(pricingActions.updateSplitListingAdvanced(dataToSend)).unwrap();
			onSuccess?.();
		} catch (error) {
			console.log(error);
		}
	};

	const handleReset = () => {
		splitInGroups ? rAdvanced({ Number_Of_Groups: 2 }) : reset(defaultValues);
	};

	useEffect(() => {
		reset(defaultValues);
		rAdvanced({ Number_Of_Groups: 2 });
	}, [inventoryItem]);

	const numOfSplitLists = Math.floor(Quantity / numOfGroups);
	const hasRest = Quantity % numOfGroups;
	const groupsOutOfBounds = +numOfGroups > Quantity;

	const intoString = hasRest
		? `${numOfGroups - 1} Lists(s) of ${numOfSplitLists} Seats + 1 Lists(s) of ${hasRest + numOfSplitLists} Seats`
		: `${numOfGroups} Lists(s) of ${numOfSplitLists} Seats`;
	const infoText = splitInGroups && !groupsOutOfBounds && numOfGroups > 1 ? intoString : "";

	const isSold = !!inventoryItem?.Invoice_ID;

	useFirstTimeInteraction({
		onInteraction: () => void 0, // Mixpanel here? This function watches the first interaction.
		dependencyArray: [splitInGroups, quantity, ticketPrice, numOfGroups],
	});

	const isHighlighted = useHighlightedSection(HighlightedSectionEnum.SplitListing);

	const formRef = useRef<HTMLFormElement>(null);
	useScrollToBottom(formRef, 300, isHighlighted);

	if (Quantity === 1) return null;
	return (
		<form
			ref={formRef}
			data-is-sold={isSold}
			onSubmit={splitInGroups ? hsAdvanced(onAdvancedSubmit) : handleSubmit(onSubmit)}
			className={styles.splitListingContainer}>
			<SubHeader isHighlighted={isHighlighted} title="Split Listing" tooltipText="Split Listing" />
			<div className={styles.inputs}>
				{!splitInGroups ? (
					<React.Fragment>
						<div className={styles.inputContainer}>
							<Input
								controlSize="sm"
								label={`Quantity (Tickets: ${Quantity})`}
								name="Quantity"
								disabled={isSubmitting}
								control={control}
								type="number"
							/>
						</div>
						<div className={styles.inputContainer}>
							<Input
								controlSize="sm"
								label={`Ticket Price (Current: $${inventoryItem?.Item_Price})`}
								inputPrefix="$"
								name="Ticket_Price"
								disabled={isSubmitting}
								control={control}
								type="number"
							/>
						</div>
					</React.Fragment>
				) : (
					<div className={styles.inputContainer}>
						<Input
							controlSize="sm"
							label="Number of Groups"
							name="Number_Of_Groups"
							disabled={isSubmitting}
							control={cAdvanced}
							type="number"
						/>
					</div>
				)}
				<div className={styles.switchContainer}>
					<Switch
						onChange={onSplitsChange}
						checked={splitInGroups}
						label="Split in Groups"
						tooltip="Split in Groups"
						tooltipId="inGroupsTooltip"
					/>
				</div>
			</div>
			<div className={styles.infoAndButton}>
				<div className={styles.info}>{infoText}</div>
				<div className={styles.buttons}>
					<Button
						isLoading={isSubmitting || submittingAdvanced}
						type="button"
						onClick={handleReset}
						variant={ButtonVariant.Secondary}
						disabled={isSubmitting || submittingAdvanced}>
						Reset
					</Button>
					<Button
						isLoading={isSubmitting || submittingAdvanced}
						type="submit"
						variant={ButtonVariant.Primary}
						disabled={isSubmitting || submittingAdvanced}>
						Split Listing
					</Button>
				</div>
			</div>
		</form>
	);
};
